import type { Socket } from "socket.io-client"
import { io } from "socket.io-client"

import type {
	LiveChatMessageData,
	LiveChatUserJoinedData,
	LiveChatUserLeftData,
} from "@productlane/lib"

import { env } from "@/env"

interface LiveChatSocketOptions {
	onConnect?: () => void
	onDisconnect?: () => void
	onReconnect?: () => void
	onUserJoined?: (data: LiveChatUserJoinedData) => void
	onUserLeft?: (data: LiveChatUserLeftData) => void
	onMessage?: (data: LiveChatMessageData) => void
	onSessionDeleted?: (data: { message: string; timestamp: string }) => void
}

// Tracks the current session for reconnect re-join
let currentSessionData: {
	sessionId: string
	name: string
	imageUrl?: string
	userId?: string
} | null = null

export function createLiveChatSocket(options: LiveChatSocketOptions) {
	const {
		onConnect,
		onDisconnect,
		onReconnect,
		onUserJoined,
		onUserLeft,
		onMessage,
		onSessionDeleted,
	} = options

	const socket = io(env.VITE_SOCKET_IO_URL, {
		path: "/io",
		transports: ["websocket"],
		reconnection: true,
		reconnectionAttempts: 10,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 5000,
		timeout: 20000,
		forceNew: false,
	})

	socket.on("connect", () => {
		onConnect?.()
		// Re-join the current session on connect (covers initial + reconnect)
		if (currentSessionData) {
			socket.emit("live-chat:join", {
				sessionId: currentSessionData.sessionId,
				name: currentSessionData.name,
				imageUrl: currentSessionData.imageUrl,
				userId: currentSessionData.userId,
			})
		}
	})

	socket.on("disconnect", () => {
		onDisconnect?.()
	})

	socket.on("connect_error", (error) => {
		console.error("Socket connection error:", error)
	})

	socket.on("reconnect", () => {
		onReconnect?.()
		if (currentSessionData) {
			socket.emit("live-chat:join", {
				sessionId: currentSessionData.sessionId,
				name: currentSessionData.name,
				imageUrl: currentSessionData.imageUrl,
				userId: currentSessionData.userId,
			})
		}
	})

	if (onUserJoined) {
		socket.on("live-chat:user-joined", onUserJoined)
	}

	if (onUserLeft) {
		socket.on("live-chat:user-left", onUserLeft)
	}

	if (onMessage) {
		socket.on("live-chat:message", onMessage)
	}

	if (onSessionDeleted) {
		socket.on("live-chat:session-deleted", onSessionDeleted)
	}

	return {
		socket,
		disconnect: () => {
			currentSessionData = null
			socket.off("connect")
			socket.off("disconnect")
			socket.off("connect_error")
			socket.off("reconnect")
			socket.off("live-chat:message")
			socket.off("live-chat:user-joined")
			socket.off("live-chat:user-left")
			socket.off("live-chat:session-deleted")
			socket.disconnect()
		},
	}
}

/**
 * Subscribe the socket to a specific session.
 * The server will auto-leave the previous room if switching sessions.
 */
export function subscribeToSession(
	socket: Socket,
	sessionId: string,
	userData: { name?: string; imageUrl?: string; userId?: string },
) {
	currentSessionData = {
		sessionId,
		name: userData.name ?? "Agent",
		imageUrl: userData.imageUrl,
		userId: userData.userId,
	}

	if (socket.connected) {
		socket.emit("live-chat:join", {
			sessionId,
			name: userData.name ?? "Agent",
			imageUrl: userData.imageUrl,
			userId: userData.userId,
		})
	}
	// If not yet connected, the connect handler will emit join
}

/**
 * Join a session and wait for the server to confirm it. The join handler reads
 * the database before it puts the socket in the room, so this is useful for ensuring the session exists before proceeding.
 */
export async function joinSessionAndWait(
	socket: Socket,
	sessionId: string,
	userData: { name?: string; imageUrl?: string; userId?: string },
): Promise<boolean> {
	currentSessionData = {
		sessionId,
		name: userData.name ?? "Agent",
		imageUrl: userData.imageUrl,
		userId: userData.userId,
	}

	try {
		const result: unknown = await socket
			.timeout(10000)
			.emitWithAck("live-chat:join", {
				sessionId,
				name: userData.name ?? "Agent",
				imageUrl: userData.imageUrl,
				userId: userData.userId,
			})
		return !!(result as { joined?: boolean } | null)?.joined
	} catch {
		return false
	}
}

/**
 * Unsubscribe from the current session without disconnecting the socket.
 * The server leaves the room and emits user-left, but does NOT finish the session.
 */
export function unsubscribeFromSession(socket: Socket) {
	currentSessionData = null

	if (socket.connected) {
		socket.emit("live-chat:unsubscribe")
	}
}
